
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useGameLogic } from "@/hooks/useGameLogic";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { GameLoadingState } from "@/components/game/GameLoadingState";
import { GameCompletedState } from "@/components/game/GameCompletedState";
import { GameActiveState } from "@/components/game/GameActiveState";

const Game = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get("id");
  const gameType = searchParams.get("type");
  const isDaily = gameType === "daily";
  const { toast } = useToast();
  const { user } = useAuth();

  const {
    rounds,
    currentWord,
    setCurrentWord,
    isRevealed,
    gameOver,
    convergenceRound,
    loading,
    isSubmitting,
    botIsThinking,
    botHasChosenWord,
    shouldAutoFocus,
    setShouldAutoFocus,
    handleSubmitWord,
    gameStatus,
    userHasSubmitted,
    wordError,
    handleClearWordError
  } = useGameLogic(gameId, user?.id);

  debugLog(DebugLevel.INFO, "Game.tsx", "Game component render state:", {
    isSubmitting,
    botIsThinking,
    botHasChosenWord,
    currentWord,
    gameOver,
    shouldAutoFocus,
    userHasSubmitted,
    wordError,
    roundsLength: rounds.length
  });

  const handleSignInToSave = () => {
    if (gameId) {
      localStorage.setItem('pendingGameToAssociate', gameId);
      navigate('/login');
    }
  };

  if (loading) {
    return <GameLoadingState />;
  }

  if (gameStatus === "diverged" || gameStatus === "completed") {
    return (
      <GameCompletedState
        isDaily={isDaily}
        rounds={rounds}
        convergenceRound={convergenceRound}
        user={user}
        onSignInToSave={handleSignInToSave}
        gameStatus={gameStatus}
      />
    );
  }

  return (
    <GameActiveState
      isDaily={isDaily}
      gameId={gameId}
      rounds={rounds}
      currentWord={currentWord}
      setCurrentWord={setCurrentWord}
      isSubmitting={isSubmitting}
      botIsThinking={botIsThinking}
      botHasChosenWord={botHasChosenWord}
      shouldAutoFocus={shouldAutoFocus}
      setShouldAutoFocus={setShouldAutoFocus}
      userHasSubmitted={userHasSubmitted}
      wordError={wordError}
      handleSubmitWord={handleSubmitWord}
      handleClearWordError={handleClearWordError}
      gameOver={gameOver}
      convergenceRound={convergenceRound}
      user={user}
      onSignInToSave={handleSignInToSave}
      gameStatus={gameStatus}
    />
  );
}

export default Game;
